// src/components/FixedScale/FixedScale.tsx

import React, { useEffect, useState } from 'react';
import styles from './FixedScale.module.css';

/**
 * Tamanho de design do site. Todo o layout de desktop é escrito assumindo
 * exatamente esta viewport; em telas menores a página inteira é reduzida
 * proporcionalmente em vez de reflowar (mesma abordagem do Clydes Launcher).
 */
export const DESIGN_WIDTH = 1920;
export const DESIGN_HEIGHT = 1080;

/**
 * Abaixo desta largura o layout mobile responsivo assume (os media queries de
 * `max-width: 999px` que já existem no CSS). Escalar um design de 1920px num
 * celular deixaria o texto ilegível.
 */
export const DESKTOP_MIN_WIDTH = 1000;

const isDesktopViewport = (): boolean =>
  typeof window !== 'undefined' && window.innerWidth >= DESKTOP_MIN_WIDTH;

/**
 * Dimensões que o conteúdo enxerga como "viewport". No desktop é sempre a
 * caixa de design; no mobile é a janela real. Componentes que dimensionam algo
 * em pixels (ex: o canvas three.js da HomePage) devem usar isto no lugar de
 * window.innerWidth/innerHeight, senão ficam fora de escala.
 */
export function getDesignViewportSize(): { width: number; height: number } {
  if (isDesktopViewport()) {
    return { width: DESIGN_WIDTH, height: DESIGN_HEIGHT };
  }
  return { width: window.innerWidth, height: window.innerHeight };
}

interface FixedScaleProps {
  children: React.ReactNode;
}

const FixedScale: React.FC<FixedScaleProps> = ({ children }) => {
  const [viewport, setViewport] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : DESIGN_WIDTH,
    height: typeof window !== 'undefined' ? window.innerHeight : DESIGN_HEIGHT,
  }));

  useEffect(() => {
    const handleResize = () => {
      setViewport({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Mobile/tablet: sem escala, o CSS responsivo existente cuida do layout.
  if (viewport.width < DESKTOP_MIN_WIDTH) {
    return <>{children}</>;
  }

  // Maior escala que ainda faz a caixa de design inteira caber na janela.
  // Nunca passa de 1: em telas maiores que 1920x1080 o layout fica no tamanho
  // original em vez de esticar.
  const scale = Math.min(
    1,
    viewport.width / DESIGN_WIDTH,
    viewport.height / DESIGN_HEIGHT
  );

  return (
    <div
      className={styles.stage}
      style={{
        width: DESIGN_WIDTH,
        height: DESIGN_HEIGHT,
        transform: `translate(-50%, -50%) scale(${scale})`,
      }}
    >
      {children}
    </div>
  );
};

export default FixedScale;
